import { type LayoutMode, CONTAINER_PADDING, CHILD_GAP, COLUMNS } from './GroupNode'

export type DropAction = 'insert-before' | 'replace' | 'insert-after'

export interface InsertionResult {
  index: number
  action: DropAction
}

/**
 * Compute the insertion index and action for a node being dragged over a group.
 * Given pointer coordinates relative to the group's top-left corner,
 * determines which grid slot the pointer is closest to and what action to take.
 *
 * Three zones within each cell (based on pointer position along the primary axis):
 * - Left/top ~25%: insert-before at this cell's index
 * - Center ~50%: replace (if slot is null), otherwise fall back to nearest insert
 * - Right/bottom ~25%: insert-after (index + 1)
 *
 * @param pointerX - X position relative to the group's top-left
 * @param pointerY - Y position relative to the group's top-left
 * @param layoutMode - The group's active layout mode
 * @param cellWidth - Width of each grid cell
 * @param cellHeight - Height of each grid cell
 * @param slotCount - Current number of slots (children) in the group
 * @param childIds - Optional array of child IDs (null = empty slot) for replace detection
 * @returns The insertion index and action
 */
export function computeInsertionIndex(
  pointerX: number,
  pointerY: number,
  layoutMode: LayoutMode,
  cellWidth: number,
  cellHeight: number,
  slotCount: number,
  childIds?: (string | null)[],
): InsertionResult {
  // Convert pointer position to grid coordinates
  // Subtract padding to get into the content area
  const contentX = pointerX - CONTAINER_PADDING
  const contentY = pointerY - CONTAINER_PADDING

  // Compute which column and row the pointer is in
  const colStep = cellWidth + CHILD_GAP
  const rowStep = cellHeight + CHILD_GAP

  // Clamp to non-negative, then compute fractional column/row
  const col = Math.max(0, contentX) / colStep
  const row = Math.max(0, contentY) / rowStep

  // Determine the cell index and fractional position within the cell
  const floorCol = Math.floor(col)
  const floorRow = Math.floor(row)
  const fracCol = col - floorCol  // 0..1 position within the cell horizontally
  const fracRow = row - floorRow  // 0..1 position within the cell vertically

  let cellIndex: number

  switch (layoutMode) {
    case 'horizontal':
      cellIndex = floorCol
      break
    case 'vertical':
      cellIndex = floorRow
      break
    case 'auto':
    default:
      cellIndex = floorRow * COLUMNS + floorCol
      break
  }

  // Clamp cell index to valid range
  cellIndex = Math.max(0, Math.min(cellIndex, slotCount - 1))

  // Determine position fraction along the primary axis
  const primaryFrac = layoutMode === 'vertical' ? fracRow : fracCol

  // Determine action based on position within the cell
  if (primaryFrac < 0.25) {
    // Left/top ~25%: insert before
    return { index: Math.max(0, Math.min(cellIndex, slotCount)), action: 'insert-before' }
  } else if (primaryFrac > 0.75) {
    // Right/bottom ~25%: insert after
    return { index: Math.max(0, Math.min(cellIndex + 1, slotCount)), action: 'insert-after' }
  } else {
    // Center ~50%: replace if the slot is null, otherwise fall back to nearest insert
    if (childIds && cellIndex < childIds.length && childIds[cellIndex] === null) {
      return { index: cellIndex, action: 'replace' }
    }
    // Occupied slot — fall back to nearest edge (left half -> insert-before, right half -> insert-after)
    if (primaryFrac < 0.5) {
      return { index: Math.max(0, Math.min(cellIndex, slotCount)), action: 'insert-before' }
    } else {
      return { index: Math.max(0, Math.min(cellIndex + 1, slotCount)), action: 'insert-after' }
    }
  }
}
